import type { IconType } from 'react-icons';
import {
  LuBriefcase,
  LuCalculator,
  LuChartLine,
  LuClipboardList,
  LuEye,
  LuGraduationCap,
  LuHandshake,
  LuLightbulb,
  LuListChecks,
  LuRadar,
  LuShield,
  LuSprout,
  LuTruck,
  LuWarehouse,
} from 'react-icons/lu';

import { PersonaMode } from '@features/navigation/personaMode';

// How a prompt resolves once the assistant backend exists. The current UI only
// fills the composer, but the tag is captured now so routing can be added later
// without rewriting the catalogue:
//   ask     - pure Q&A / research, answerable by the assistant
//   act     - produces an artifact or opens a real surface (hedge plan, procure form…)
//   connect - needs an integration or external counterparty (Datana, markets, monitors)
export type PromptKind = 'ask' | 'act' | 'connect';

export interface SuggestedPrompt {
  text: string;
  kind: PromptKind[];
}

export interface SuggestionCategory {
  id: string;
  label: string;
  icon: IconType;
  prompts: SuggestedPrompt[];
}

export interface ModeAssistantConfig {
  // Placeholder for the composer input, tuned to the mode's vocabulary.
  composerPlaceholder: string;
  // Title shown on the suggestion panel header (the "Try Computer" analogue).
  panelTitle: string;
  categories: SuggestionCategory[];
}

const ANALYST_CONFIG: ModeAssistantConfig = {
  composerPlaceholder: 'Ask about prices, hedging, or markets…',
  panelTitle: 'Analyst tools',
  categories: [
    {
      id: 'hedge-exposure',
      label: 'Hedge my exposure',
      icon: LuShield,
      prompts: [
        {
          text: 'I buy 20 tonnes of maize a month — build me a hedge against price spikes',
          kind: ['act'],
        },
        { text: 'How exposed am I if cocoa rises 15% this quarter?', kind: ['ask'] },
        { text: 'Compare hedging cocoa with weekly vs monthly contracts', kind: ['ask'] },
      ],
    },
    {
      id: 'read-market',
      label: 'Read the market',
      icon: LuChartLine,
      prompts: [
        {
          text: "What's the market pricing for rice in Accra over the next 3 months?",
          kind: ['connect'],
        },
        {
          text: 'Which commodities have the most disagreement among predictors right now?',
          kind: ['ask'],
        },
        {
          text: "Summarize this week's price moves across agriculture and flag what changed",
          kind: ['act'],
        },
      ],
    },
    {
      id: 'build-thesis',
      label: 'Build a thesis',
      icon: LuLightbulb,
      prompts: [
        { text: 'Size the market for rice consumption in Accra', kind: ['ask'] },
        {
          text: 'Analyze why maize prices spiked last season and whether it repeats',
          kind: ['ask'],
        },
        { text: 'Draft a price-outlook memo I can share with my buyers', kind: ['act'] },
      ],
    },
    {
      id: 'learn-ropes',
      label: 'Learn the ropes',
      icon: LuGraduationCap,
      prompts: [
        { text: 'Explain hedging like I run a bakery, not a trading desk', kind: ['ask'] },
        { text: 'What does this prediction-market question actually mean for me?', kind: ['ask'] },
        { text: 'Quiz me on the risks of over-hedging until I get it', kind: ['ask'] },
      ],
    },
    {
      id: 'monitor-alert',
      label: 'Monitor & alert',
      icon: LuEye,
      prompts: [
        {
          text: 'Watch cocoa and alert me when the implied price crosses GHS1,200',
          kind: ['connect'],
        },
        { text: 'Track rice consumption with a weekly report', kind: ['connect'] },
        { text: 'Flag news that could move the commodities I hold', kind: ['connect'] },
      ],
    },
  ],
};

const OPERATOR_CONFIG: ModeAssistantConfig = {
  composerPlaceholder: 'Type / for actions, @ for your business data…',
  panelTitle: 'Operator tools',
  categories: [
    {
      id: 'plan-procurement',
      label: 'Plan my procurement',
      icon: LuClipboardList,
      prompts: [
        { text: 'Itemize my menu into the ingredients I need to buy', kind: ['act', 'connect'] },
        {
          text: "From last month's spend, build next month's procurement list",
          kind: ['act', 'connect'],
        },
        { text: "I'm opening a second branch — what should I stock, and how much?", kind: ['act'] },
      ],
    },
    {
      id: 'source-fulfill',
      label: 'Source & fulfill',
      icon: LuTruck,
      prompts: [
        { text: 'Publish a demand signal for 5 tonnes of rice this month', kind: ['act'] },
        { text: 'Find a warehouse near Tema that can store maize', kind: ['act'] },
        { text: 'Which suppliers can meet my need list, and at what price?', kind: ['connect'] },
      ],
    },
    {
      id: 'run-business',
      label: 'Run the business',
      icon: LuBriefcase,
      prompts: [
        {
          text: 'What are best practices to run my business — tools and processes?',
          kind: ['ask'],
        },
        {
          text: 'Draft a partnership proposal to a target business to supply them weekly',
          kind: ['act', 'connect'],
        },
        { text: 'Size the market for my business idea', kind: ['ask'] },
      ],
    },
    {
      id: 'organize-operations',
      label: 'Organize operations',
      icon: LuListChecks,
      prompts: [
        { text: 'Sort my procurement tasks and prioritize this week', kind: ['act'] },
        {
          text: 'Turn my recurring rice order into an automated workflow',
          kind: ['act', 'connect'],
        },
        { text: 'Triage my supplier messages and draft responses', kind: ['act'] },
      ],
    },
    {
      id: 'know-numbers',
      label: 'Know my numbers',
      icon: LuCalculator,
      prompts: [
        {
          text: 'What is the GDP of Ghana? How are we doing economically? Key stats?',
          kind: ['ask'],
        },
        { text: "Model my menu's margins if maize rises 10%", kind: ['act', 'connect'] },
        { text: 'Build a budget tracker from my spending', kind: ['act', 'connect'] },
      ],
    },
  ],
};

const SUPPLIER_CONFIG: ModeAssistantConfig = {
  composerPlaceholder: 'Find demand or commit supply…',
  panelTitle: 'Supplier tools',
  categories: [
    {
      id: 'find-demand',
      label: 'Find demand',
      icon: LuRadar,
      prompts: [
        { text: 'Show me open demand for maize within 100km of Kumasi', kind: ['connect'] },
        { text: 'Which sectors have the most unmet demand right now?', kind: ['ask'] },
        {
          text: 'Match my available stock to buyers who need it this week',
          kind: ['act', 'connect'],
        },
      ],
    },
    {
      id: 'commit-quote',
      label: 'Commit & quote',
      icon: LuHandshake,
      prompts: [
        { text: 'Help me price a 10-tonne rice commitment competitively', kind: ['ask'] },
        { text: 'Draft a supply commitment against this demand signal', kind: ['act'] },
        { text: 'Should I lock a fixed price or hedge my margin on this order?', kind: ['ask'] },
      ],
    },
    {
      id: 'plan-capacity',
      label: 'Plan supply & capacity',
      icon: LuWarehouse,
      prompts: [
        {
          text: 'Itemize my warehouse stock and what I can supply next month',
          kind: ['act', 'connect'],
        },
        { text: 'Find storage near my buyers to cut delivery time', kind: ['act'] },
        { text: 'What can I realistically commit without overselling capacity?', kind: ['ask'] },
      ],
    },
    {
      id: 'grow-book',
      label: 'Grow my book',
      icon: LuSprout,
      prompts: [
        { text: 'Which commodities should I add based on the demand I am seeing?', kind: ['ask'] },
        { text: 'Draft a standing-order proposal to a recurring buyer', kind: ['act', 'connect'] },
        { text: 'What are best practices for reliable fulfillment and ratings?', kind: ['ask'] },
      ],
    },
    {
      id: 'monitor-situation',
      label: 'Monitor the situation',
      icon: LuEye,
      prompts: [
        { text: 'Alert me when new demand for cocoa appears in my region', kind: ['connect'] },
        { text: 'Track price trends so I quote at the right margin weekly', kind: ['connect'] },
        {
          text: 'Watch for disruptions (weather, fuel) that affect my deliveries',
          kind: ['connect'],
        },
      ],
    },
  ],
};

const FINANCE_CONFIG: ModeAssistantConfig = {
  composerPlaceholder: 'Create a payment link or pay a supplier…',
  panelTitle: 'Finance tools',
  categories: [
    {
      id: 'collect-payments',
      label: 'Collect payments',
      icon: LuClipboardList,
      prompts: [
        { text: 'Create a payment link for GHS 10,000 from Customer X', kind: ['act'] },
        { text: 'Let this invoice be paid in bits until the full amount is collected', kind: ['act'] },
        { text: 'Show me which payment links are still open and how much is left', kind: ['ask'] },
      ],
    },
    {
      id: 'pay-suppliers',
      label: 'Pay suppliers',
      icon: LuTruck,
      prompts: [
        { text: 'Pay supplier X 500 USDC once this link is fulfilled', kind: ['act'] },
        { text: 'Send 1,200 USDC to a supplier wallet on Polygon', kind: ['act'] },
        { text: 'What is my available USDC balance to pay suppliers?', kind: ['ask'] },
      ],
    },
    {
      id: 'track-money',
      label: 'Track the money',
      icon: LuListChecks,
      prompts: [
        { text: 'Reconcile what I collected in GHS against what I sent in USDC', kind: ['ask'] },
        { text: 'Show me payouts that are pending confirmation', kind: ['ask'] },
        { text: 'Alert me when a payment link gets fully paid', kind: ['connect'] },
      ],
    },
  ],
};

export const MODE_ASSISTANT_CONFIG: Record<PersonaMode, ModeAssistantConfig> = {
  [PersonaMode.Analyst]: ANALYST_CONFIG,
  [PersonaMode.Operator]: OPERATOR_CONFIG,
  [PersonaMode.Supplier]: SUPPLIER_CONFIG,
  [PersonaMode.Finance]: FINANCE_CONFIG,
};

// The same shape backs both the mode-keyed home surface and the per-page surfaces
// below; the alias just reads better at page call sites.
export type AssistantSurfaceConfig = ModeAssistantConfig;

// Commodity explorer / Hedge page (Analyst).
export const HEDGE_ASSISTANT_CONFIG: AssistantSurfaceConfig = {
  composerPlaceholder: 'Ask about a commodity, its price, or how to hedge it…',
  panelTitle: 'Hedge tools',
  categories: [
    {
      id: 'build-hedge',
      label: 'Build a hedge',
      icon: LuShield,
      prompts: [
        { text: 'Hedge my maize purchases for the next 3 months', kind: ['act'] },
        { text: 'Whats the cheapest way to cap my cocoa cost?', kind: ['ask'] },
        { text: 'Build a hedge for 10 tonnes of rice', kind: ['act'] },
      ],
    },
    {
      id: 'understand-commodity',
      label: 'Understand a commodity',
      icon: LuChartLine,
      prompts: [
        { text: 'Why has cocoa moved this week?', kind: ['connect'] },
        { text: 'What drives maize prices in Ghana?', kind: ['ask'] },
        { text: 'Compare cocoa and maize volatility', kind: ['ask'] },
      ],
    },
    {
      id: 'size-exposure',
      label: 'Size my exposure',
      icon: LuCalculator,
      prompts: [
        { text: 'How much do I lose if rice rises 20%?', kind: ['ask'] },
        { text: "What's my exposure across every commodity I buy?", kind: ['connect'] },
        { text: 'How much should I hedge versus leave open?', kind: ['ask'] },
      ],
    },
    {
      id: 'learn-hedging',
      label: 'Learn hedging',
      icon: LuGraduationCap,
      prompts: [
        { text: "Explain this commodity's contract in plain terms", kind: ['ask'] },
        { text: 'When does hedging actually hurt me?', kind: ['ask'] },
        { text: 'Quiz me on hedging basics', kind: ['ask'] },
      ],
    },
  ],
};

// Prediction markets / Predict page (Analyst).
export const PREDICT_ASSISTANT_CONFIG: AssistantSurfaceConfig = {
  composerPlaceholder: 'Ask about a market, the odds, or a forecast…',
  panelTitle: 'Prediction tools',
  categories: [
    {
      id: 'read-odds',
      label: 'Read the odds',
      icon: LuChartLine,
      prompts: [
        { text: 'Which markets are predictors most confident about this week?', kind: ['connect'] },
        { text: "What's the implied price for cocoa next month?", kind: ['connect'] },
        { text: 'Where do predictors disagree the most right now?', kind: ['ask'] },
      ],
    },
    {
      id: 'form-view',
      label: 'Form a view',
      icon: LuLightbulb,
      prompts: [
        { text: 'Make the case for and against rice rising next month', kind: ['ask'] },
        { text: 'What would have to happen for cocoa to hit GHS1,200?', kind: ['ask'] },
        { text: 'Should I expect maize to stay above GHS800?', kind: ['ask'] },
      ],
    },
    {
      id: 'track-outcomes',
      label: 'Track outcomes',
      icon: LuEye,
      prompts: [
        { text: 'Alert me when a market crosses 70% probability', kind: ['connect'] },
        { text: 'Track the weekly rice market for me', kind: ['connect'] },
        { text: 'Notify me when new cocoa markets open', kind: ['connect'] },
      ],
    },
    {
      id: 'learn-markets',
      label: 'Learn prediction markets',
      icon: LuGraduationCap,
      prompts: [
        { text: 'How do these prediction markets work?', kind: ['ask'] },
        { text: "What does this question's payout mean for me?", kind: ['ask'] },
        { text: 'Quiz me on reading probabilities', kind: ['ask'] },
      ],
    },
  ],
};

// Explore demand signals / Supply chains page (Supplier).
export const DEMAND_ASSISTANT_CONFIG: AssistantSurfaceConfig = {
  composerPlaceholder: 'Find demand or match it to your supply…',
  panelTitle: 'Demand tools',
  categories: [
    {
      id: 'find-demand',
      label: 'Find demand',
      icon: LuRadar,
      prompts: [
        { text: 'Show open demand for maize near Kumasi', kind: ['connect'] },
        { text: 'Which sectors have the most unmet demand right now?', kind: ['ask'] },
        { text: "Who's buying rice this week?", kind: ['connect'] },
      ],
    },
    {
      id: 'match-supply',
      label: 'Match my supply',
      icon: LuHandshake,
      prompts: [
        { text: 'Match my available stock to buyers who need it', kind: ['act', 'connect'] },
        { text: 'Draft a commitment against this demand signal', kind: ['act'] },
        { text: 'Which signals fit what I can actually supply?', kind: ['connect'] },
      ],
    },
    {
      id: 'plan-capacity',
      label: 'Plan capacity',
      icon: LuWarehouse,
      prompts: [
        { text: 'What can I commit without overselling capacity?', kind: ['ask'] },
        { text: 'Itemize my warehouse stock', kind: ['act', 'connect'] },
        { text: 'Find storage near my buyers to cut delivery time', kind: ['act'] },
      ],
    },
    {
      id: 'watch-market',
      label: 'Watch the market',
      icon: LuEye,
      prompts: [
        { text: 'Alert me when new demand for cocoa appears in my region', kind: ['connect'] },
        { text: 'Track demand trends in my sector', kind: ['connect'] },
        { text: 'Watch for supply disruptions near me', kind: ['connect'] },
      ],
    },
  ],
};
